import { Extensions } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import { clamp } from 'lodash';
import { MutableRefObject, useCallback, useMemo } from 'react';

import type { TiptapCellEditingSession } from '../../../../common/table/TableTypes';
import type { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import useClientCommonTiptapEditorExtensions from '../../../components/tiptapEditor/useClientCommonTiptapEditorExtensions';
import FormulaCellHighlightTiptapExtension from '../plugins/FormulaCellReferenceHotPlugin/FormulaCellHighlightTiptapExtension';
import { MNHotInstance } from '../tableHotInstanceTypes';
import TableCellFinishEditTiptapExtension from './TableCellFinishEditTiptapExtension';

type UseTableCellEditorTiptapExtensionsArgs = {
    isEditable: boolean;
    saveContent?: SaveTiptapEditorContentFunction;
    hotTableInstanceRef: MutableRefObject<MNHotInstance<TiptapContent, TiptapContent> | null>;
    cellEditingSessionRef: MutableRefObject<TiptapCellEditingSession | null>;
};

export const useTableCellEditorTiptapExtensions = ({
    isEditable,
    saveContent,
    hotTableInstanceRef,
    cellEditingSessionRef,
}: UseTableCellEditorTiptapExtensionsArgs): Extensions => {
    const commonExtensions = useClientCommonTiptapEditorExtensions({
        saveContent,
        // The cell editor dispatches its edits to Handsontable, not redux element update,
        // so a low debounce time is safe. Things like the formula autocomplete popup will wait
        // for the debounce before displaying, so lower = better.
        getDebounceTime: () => 1,
        // The editor can be non-editable in some situations, eg when a formatting popup is open
        isEditable,
    });

    const onCommitEdit = useCallback(() => {
        const hotInstance = hotTableInstanceRef.current;
        const session = cellEditingSessionRef.current;
        if (!hotInstance || !session) return;

        const activeEditor = hotInstance.getActiveEditor();
        if (!activeEditor) return;

        const { row, col } = session.cellCoords;
        const nextRow = clamp(row + 1, 0, hotInstance.countRows() - 1);

        // Because we suppress the default enter behaviour (in TableCellFinishEditTiptapExtension),
        // we need to manually finish editing and move the selection to the next cell.
        activeEditor.finishEditing(false, false, () => {
            hotInstance.selectCell(nextRow, col);
        });
    }, [cellEditingSessionRef, hotTableInstanceRef]);

    const onCancelEdit = useCallback(() => {
        const hotInstance = hotTableInstanceRef.current;
        const activeEditor = hotInstance?.getActiveEditor();
        if (!activeEditor) return;

        // true param => discard changes
        activeEditor.finishEditing(true);
    }, [hotTableInstanceRef]);

    return useMemo(
        () =>
            [
                Document,
                FormulaCellHighlightTiptapExtension,
                TableCellFinishEditTiptapExtension.configure({ onCommitEdit, onCancelEdit }),
                ...commonExtensions,
            ] as Extensions,
        [commonExtensions, onCommitEdit, onCancelEdit],
    );
};

export const useTableCellRendererTiptapExtensions = (): Extensions => {
    const commonExtensions = useClientCommonTiptapEditorExtensions({
        isEditable: false,
    });

    return useMemo(
        () => [Document, FormulaCellHighlightTiptapExtension, ...commonExtensions] as Extensions,
        [commonExtensions],
    );
};
